import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { StyleShadowRoot } from "@redotech/react-util/style";
import { SignUpFormGoal } from "@redotech/redo-model/signup-form";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { getFormsByGoal } from "@redotech/shopify-sdk/rpc/schema/forms/get-forms-by-goal";
import { getProductByHandle } from "@redotech/shopify-sdk/rpc/schema/get-product-by-handle";
import { getProductById } from "@redotech/shopify-sdk/rpc/schema/get-product-by-id";
import {
  convertShopifyIdToGid,
  parseNumericIdFromShopifyId,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { memoizeAsyncV2 } from "@redotech/util/memoize";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RedoShopifyRpcClientContext } from "../../../client/redo-shopify-rpc-client";
import STYLE_MANAGER from "../../../style-manager";
import { getVariantIdFromUrl } from "../../../utils";
import { BackInStockFormModal } from "./back-in-stock-form-modal";
import { useBackInStockRoot } from "./use-back-in-stock-root";

/**
 * Hook to detect URL changes for variant selection
 * @param callback Function to call when URL changes
 */
export function useUrlChangeDetection(callback: () => void) {
  useEffect(() => {
    callback();

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      callback();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      callback();
    };

    // Also listen for popstate events (browser back/forward)
    addEventListener("popstate", callback);

    // Cleanup
    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      removeEventListener("popstate", callback);
    };
  }, [callback]);
}

/**
 * Hook to check if the current product variant is out of stock
 * @param rpcClient The RedoShopifyRpcClient instance
 * @returns Object containing stock status and loading state
 */
export function useVariantStockStatus(
  rpcClient: RedoShopifyRpcClient | undefined,
): {
  isOutOfStock: boolean;
  isCheckingStock: boolean;
  productId: string | null;
  variantId: string | null;
} {
  const [isOutOfStock, setIsOutOfStock] = useState<boolean>(false);
  const [isCheckingStock, setIsCheckingStock] = useState<boolean>(true);
  const [productId, setProductId] = useState<string | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);

  const getProductByHandleMemoized = useMemo(
    () =>
      memoizeAsyncV2(
        async (productHandle: string) => {
          if (!rpcClient) {
            return undefined;
          }

          const product = await rpcClient(getProductByHandle, {
            productHandle,
          });
          return product;
        },
        { cacheSize: 1, keyFn: (input) => input[0] },
      ),
    [rpcClient],
  );

  const getProductByIdMemoized = useMemo(
    () =>
      memoizeAsyncV2(
        async (productId: string) => {
          if (!rpcClient) {
            return undefined;
          }

          const product = await rpcClient(getProductById, { productId });
          return { ...product, id: productId };
        },
        { cacheSize: 1, keyFn: (input) => input[0] },
      ),
    [rpcClient],
  );

  const checkCurrentVariantStock = useCallback(async () => {
    if (!rpcClient) {
      setIsCheckingStock(false);
      return;
    }

    try {
      setIsCheckingStock(true);

      const variantId = getVariantIdFromUrl(location.href);
      const productHandle =
        location.pathname.match(/\/products\/([^/?]+)/)?.[1];

      let product;
      let productId: string | null = null;

      if (productHandle) {
        product = await getProductByHandleMemoized(productHandle);
        productId = product?.id ?? null;
      } else {
        productId =
          (globalThis as any).ShopifyAnalytics?.meta?.product?.id?.toString() ??
          null;
        if (productId) {
          product = await getProductByIdMemoized(productId);
        }
      }

      if (!productId) {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
        return;
      }

      if (!product || !product.variants || product.variants.length === 0) {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
        return;
      }

      if (variantId) {
        const variant = product.variants.find(
          (v: (typeof product.variants)[number]) =>
            String(v.id) ===
            convertShopifyIdToGid(variantId, ShopifyIdType.ProductVariant),
        );
        if (variant) {
          setIsOutOfStock(!variant.available);
          setIsCheckingStock(false);
          setProductId(productId);
          setVariantId(variantId);
          return;
        }
      }

      const firstVariant =
        product.variants.find(
          (v: (typeof product.variants)[number]) => v.available,
        ) ?? product.variants[0];

      if (firstVariant) {
        setIsOutOfStock(!firstVariant.available);
        setIsCheckingStock(false);
        setProductId(productId);
        setVariantId(parseNumericIdFromShopifyId(firstVariant.id));
      } else {
        setIsOutOfStock(false);
      }

      setIsCheckingStock(false);
    } catch (error) {
      console.error("Error checking variant stock status:", error);
      setIsOutOfStock(false);
      setIsCheckingStock(false);
    }
  }, [rpcClient, getProductByHandleMemoized, getProductByIdMemoized]);

  useUrlChangeDetection(checkCurrentVariantStock);

  return { isOutOfStock, isCheckingStock, productId, variantId };
}

const BackInStockContent = memo(function BackInStockContent({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}) {
  const rpcClient = useRequiredContext(RedoShopifyRpcClientContext);
  const { isOutOfStock, isCheckingStock, productId, variantId } =
    useVariantStockStatus(rpcClient);

  const shopifyCustomerId = useMemo(
    () =>
      (
        globalThis as any
      ).ShopifyAnalytics?.meta?.page?.customerId?.toString() ?? null,
    [],
  );

  // Load back in stock form
  const backInStockFormLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    const forms = await rpcClient(getFormsByGoal, {
      goal: SignUpFormGoal.BACK_IN_STOCK,
    });
    return forms.filter((f: (typeof forms)[number]) => f.publishedAt);
  }, [rpcClient]);

  const [backInStockForm, setBackInStockForm] = useState<
    { id: string; publishedAt: Date | null; form: SignupForm } | undefined
  >(undefined);

  useEffect(() => {
    if (backInStockFormLoad.value) {
      setBackInStockForm(backInStockFormLoad.value[0]);
    }
  }, [backInStockFormLoad.value]);

  if (isCheckingStock || !backInStockForm || !isOutOfStock) {
    return null;
  }

  return (
    <BackInStockFormModal
      brandKit={autocheck.brandKit}
      client={rpcClient}
      form={backInStockForm.form}
      formId={backInStockForm.id}
      productId={productId}
      shopifyCustomerId={shopifyCustomerId}
      variantId={variantId}
    />
  );
});

export const ProductBackInStockStorefront = memo(
  function ProductBackInStockStorefront({
    autocheck,
  }: {
    autocheck: WidgetAutocheck;
  }) {
    const backInStockRoots = useBackInStockRoot();

    if (backInStockRoots.length === 0) {
      return null;
    }

    return (
      <>
        {backInStockRoots.map((backInStockRoot, index) => (
          <StyleShadowRoot
            key={index}
            shadowRoot={backInStockRoot.root}
            styleManager={STYLE_MANAGER}
          >
            <BackInStockContent autocheck={autocheck} />
          </StyleShadowRoot>
        ))}
      </>
    );
  },
);
